import {useEffect} from 'react';
import {useQuery} from '@apollo/client';
import {
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Switch,
  Tag,
} from 'antd';
import {DeleteOutlined, PlusOutlined} from '@ant-design/icons';
import {toast} from 'react-toastify';
import {walletCurrencies} from 'utils/mock';
import {
  GET_ALL_SUBSCRIPTION_PLANS,
  GET_PLAN_SUBSCRIPTIONS_CATALOGS,
} from 'pages/adminPlans/subscriptionSetting/resolvers';
import styles from './fullScreenPopup.module.scss';

// Matches the renewal-reminder discounts already used in the subscription plan area.
const DISCOUNT_TYPES = [
  {value: 'FIXED', label: 'Fixed amount'},
  {value: 'VARIABLE', label: 'Variable (%)'},
];

const currencyOptions = walletCurrencies.map(currency => ({
  value: currency.key,
  label: `${currency.key} (${currency.symbol})`,
  symbol: currency.symbol,
}));

const formatMoney = (symbol, amount) =>
  `${symbol || ''}${Number(amount).toLocaleString(undefined, {
    maximumFractionDigits: 2,
  })}`;

// Pick the first currency not already used by this tab's discounts.
const nextCurrency = used =>
  currencyOptions.find(opt => !used.includes(opt.value))?.value ||
  currencyOptions[0]?.value;

// Catalog prices persist their discount as PERCENTAGE/FIXED/NONE, but the
// renewal rows here speak VARIABLE/FIXED. Translate a catalog price's saved
// discount into a row, or null when there's effectively nothing to carry over.
const CATALOG_DISCOUNT_TO_ROW_TYPE = {PERCENTAGE: 'VARIABLE', FIXED: 'FIXED'};

const existingDiscountFromPrice = price => {
  const discount = price?.discount;
  if (!discount || discount.type === 'NONE') return null;
  if (discount.value === null || discount.value === undefined) return null;
  const type = CATALOG_DISCOUNT_TO_ROW_TYPE[discount.type];
  if (!type) return null;
  return {type, value: Number(discount.value)};
};

// Normalize a plan/purpose identifier ("DABA_PRO", "Daba Pro", "daba-pro") to a
// bare comparable token so a purpose can be lined up with its subscription plan.
const normalizeKey = value =>
  (value || '')
    .toString()
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

/**
 * Plan + catalog-tabs picker for a full screen popup with a Daba Pro / Daba
 * Academy purpose. Each catalog tab can carry a list of discounts — one per
 * currency. Plans come from GET_ALL_SUBSCRIPTION_PLANS; catalog tabs are fetched
 * per selected plan. Submits as popupConfig.subscriptionRenewal, where each
 * tab's appliedDiscount is an array of {type, value, currency}.
 */
export const SubscriptionRenewalSection = ({form}) => {
  const planKey = Form.useWatch(['subscriptionRenewal', 'planKey'], form);
  const catalogTabs =
    Form.useWatch(['subscriptionRenewal', 'catalogTabs'], form) || [];
  // Purpose (DABA_PRO / DABA_ACADEMY) gates which subscription plan can be picked.
  const purpose = Form.useWatch('purpose', form);

  // Once a plan is picked, seed the first catalog tab so the catalog/discount
  // controls are visible right away instead of an empty "Add" button.
  useEffect(() => {
    if (!planKey) return;
    const tabs = form.getFieldValue(['subscriptionRenewal', 'catalogTabs']);
    if (!tabs || tabs.length === 0) {
      form.setFieldValue(
        ['subscriptionRenewal', 'catalogTabs'],
        [
          {
            catalogKey: undefined,
            isDefault: true,
            applyDiscount: false,
            discounts: [],
          },
        ],
      );
    }
  }, [planKey, form]);

  const {data: plansData, loading: loadingPlans} = useQuery(
    GET_ALL_SUBSCRIPTION_PLANS,
    {
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  const {data: catalogData, loading: loadingCatalogs} = useQuery(
    GET_PLAN_SUBSCRIPTIONS_CATALOGS,
    {
      skip: !planKey,
      variables: {filter: {plan: planKey}},
      fetchPolicy: 'no-cache',
      errorPolicy: 'all',
      onError: error => toast.error(error.message),
    },
  );

  const plansResponse = plansData?.getAllSubscriptionPlans;
  const plans =
    plansResponse?.__typename === 'ResponseWithDabaSubscriptionPlans'
      ? plansResponse.data || []
      : [];
  // A subscription plan belongs to the active purpose when its key or name
  // carries that product's distinctive token (pro / academy). Matching the
  // token — not the full "dabapro" string — keeps a Daba Pro purpose from ever
  // offering the Daba Academy plan (and vice versa), regardless of how the plan
  // key is formatted on the backend.
  const purposeToken = normalizeKey(purpose).replace(/^daba/, '');
  const planMatchesPurpose = plan =>
    !purposeToken ||
    [plan?.key, plan?.name].some(field =>
      normalizeKey(field).includes(purposeToken),
    );
  const planOptions = plans.filter(planMatchesPurpose).map(plan => ({
    value: plan.key,
    label: plan.name || plan.key,
  }));

  const catalogResponse = catalogData?.getSubscriptionPlansCatalog;
  const catalogItems =
    catalogResponse?.__typename === 'ResponseWithSubscriptionCatalog'
      ? catalogResponse.data || []
      : [];
  const catalogOptions = catalogItems.map(item => ({
    value: item.key,
    label: (item.label && (item.label.EN || item.label.FR)) || item.key,
  }));

  // The catalog item may already carry a discount for a given currency. Surface
  // it (mapped to a renewal row) so the value field can be pre-filled instead of
  // starting empty — the admin then knowingly overrides it for this offer.
  const lookupExistingDiscount = (catalogKey, currency) => {
    if (!catalogKey || !currency) return null;
    const item = catalogItems.find(catalog => catalog.key === catalogKey);
    const price = item?.prices?.find(p => p.currency === currency);
    return existingDiscountFromPrice(price);
  };

  // If the purpose changes to one the current plan no longer belongs to, drop
  // the stale plan (and its catalog tabs). Guarded on plans being loaded so an
  // edit-mode hydration of a matching plan is never wrongly cleared.
  useEffect(() => {
    if (!purpose || !plans.length) return;
    const currentPlan = form.getFieldValue(['subscriptionRenewal', 'planKey']);
    if (!currentPlan) return;
    const stillValid = plans.some(
      plan => plan.key === currentPlan && planMatchesPurpose(plan),
    );
    if (!stillValid) {
      form.setFieldValue(['subscriptionRenewal', 'planKey'], undefined);
      form.setFieldValue(['subscriptionRenewal', 'catalogTabs'], []);
    }
  }, [purpose, plans, form]);

  // Only one tab can be the default. Mutate the existing array in place (same
  // pattern as the working discount toggle) so antd's Form.List propagates the
  // change to every row's controlled switch — turning one on forces the rest off.
  const setDefaultTab = (rowIndex, checked) => {
    const tabs =
      form.getFieldValue(['subscriptionRenewal', 'catalogTabs']) || [];
    for (let index = 0; index < tabs.length; index += 1) {
      if (checked) {
        tabs[index] = {...tabs[index], isDefault: index === rowIndex};
      } else if (index === rowIndex) {
        tabs[index] = {...tabs[index], isDefault: false};
      }
    }
    form.setFieldValue(['subscriptionRenewal', 'catalogTabs'], tabs);
  };

  // Toggling "Apply discount" on seeds a first currency row so the controls
  // show — pre-filled from the catalog's existing discount when there is one.
  const setApplyDiscount = (rowIndex, checked) => {
    const tabs =
      form.getFieldValue(['subscriptionRenewal', 'catalogTabs']) || [];
    const tab = tabs[rowIndex] || {};
    let {discounts} = tab;
    if (checked && !(tab.discounts && tab.discounts.length)) {
      const currency = nextCurrency([]);
      const existing = lookupExistingDiscount(tab.catalogKey, currency);
      discounts = [
        {
          type: existing?.type || 'FIXED',
          value: existing ? existing.value : null,
          currency,
        },
      ];
    }
    tabs[rowIndex] = {...tab, applyDiscount: checked, discounts};
    form.setFieldValue(['subscriptionRenewal', 'catalogTabs'], tabs);
  };

  // Changing the catalog re-pulls the saved discount into each currency row —
  // discounts live per catalog + currency, so the previous catalog's values no
  // longer apply.
  const handleCatalogChange = (rowIndex, catalogKey) => {
    const tabs =
      form.getFieldValue(['subscriptionRenewal', 'catalogTabs']) || [];
    const tab = tabs[rowIndex];
    if (!tab) return;
    const discounts = (tab.discounts || []).map(disc => {
      const existing = lookupExistingDiscount(catalogKey, disc?.currency);
      return existing
        ? {...disc, type: existing.type, value: existing.value}
        : disc;
    });
    tabs[rowIndex] = {...tab, catalogKey, discounts};
    form.setFieldValue(['subscriptionRenewal', 'catalogTabs'], tabs);
  };

  // Picking a currency pre-fills the row from that catalog price's saved
  // discount, when one exists.
  const handleCurrencyChange = (rowIndex, discIndex, currency) => {
    const tabs =
      form.getFieldValue(['subscriptionRenewal', 'catalogTabs']) || [];
    const tab = tabs[rowIndex];
    if (!tab) return;
    const discounts = [...(tab.discounts || [])];
    const existing = lookupExistingDiscount(tab.catalogKey, currency);
    discounts[discIndex] = existing
      ? {
          ...discounts[discIndex],
          currency,
          type: existing.type,
          value: existing.value,
        }
      : {...discounts[discIndex], currency};
    tabs[rowIndex] = {...tab, discounts};
    form.setFieldValue(['subscriptionRenewal', 'catalogTabs'], tabs);
  };

  const renderPreview = (tabIndex, discIndex) => {
    const tab = catalogTabs[tabIndex] || {};
    const disc = (tab.discounts || [])[discIndex] || {};
    const item = catalogItems.find(catalog => catalog.key === tab.catalogKey);
    const price = item?.prices?.find(p => p.currency === disc.currency);
    const symbol =
      currencyOptions.find(opt => opt.value === disc.currency)?.symbol || '';

    if (!tab.catalogKey || !disc.currency) {
      return <span className={styles.previewMuted}>Pick a currency</span>;
    }
    if (price?.amount === undefined || price?.amount === null) {
      return (
        <span className={styles.previewError}>
          No catalog price for {disc.currency}
        </span>
      );
    }
    if (disc.value === undefined || disc.value === null || disc.value === '') {
      return (
        <span className={styles.previewMuted}>
          {formatMoney(symbol, price.amount)} → enter a value
        </span>
      );
    }

    const base = Number(price.amount);
    const newPrice =
      disc.type === 'VARIABLE'
        ? base - (base * Number(disc.value)) / 100
        : base - Number(disc.value);

    return (
      <span>
        <span className={styles.priceStrike}>{formatMoney(symbol, base)}</span>{' '}
        <Tag color="green">{formatMoney(symbol, Math.max(0, newPrice))}</Tag>
      </span>
    );
  };

  return (
    <div className={styles.sectionCard}>
      <div className={styles.sectionHeader}>
        <h3>Subscription Plan</h3>
        <span>
          Pick the plan and the catalog tabs to offer, each with optional
          discounts per currency.
        </span>
      </div>

      <Form.Item
        label="Subscription Plan"
        name={['subscriptionRenewal', 'planKey']}
        className={styles.fieldWidth}
        rules={[{required: true, message: 'Select a subscription plan'}]}>
        <Select
          showSearch
          optionFilterProp="label"
          loading={loadingPlans}
          placeholder="Select a plan"
          options={planOptions}
        />
      </Form.Item>

      <Form.List name={['subscriptionRenewal', 'catalogTabs']}>
        {(fields, {add, remove}) => (
          <div>
            {!fields.length ? (
              <p className={styles.emptyHint}>
                No catalog tabs yet. Add at least one tab to upsell.
              </p>
            ) : null}
            {fields.map(field => {
              const row = catalogTabs[field.name] || {};
              return (
                <div key={field.key} className={styles.catalogTab}>
                  <Form.Item name={[field.name, 'id']} hidden>
                    <Input />
                  </Form.Item>
                  <div className={styles.catalogTabHeader}>
                    <div className={styles.defaultToggle}>
                      <Switch
                        size="small"
                        checked={!!row.isDefault}
                        onChange={checked => setDefaultTab(field.name, checked)}
                      />
                      <span>Default tab</span>
                    </div>
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => remove(field.name)}
                    />
                  </div>
                  <Row gutter={16}>
                    <Col xs={24} md={12}>
                      <Form.Item
                        label="Catalog tab"
                        name={[field.name, 'catalogKey']}
                        rules={[
                          {required: true, message: 'Select a catalog tab'},
                        ]}>
                        <Select
                          showSearch
                          optionFilterProp="label"
                          loading={loadingCatalogs}
                          disabled={!planKey}
                          placeholder={
                            planKey
                              ? 'Select a catalog tab'
                              : 'Select a plan first'
                          }
                          options={catalogOptions}
                          onChange={value =>
                            handleCatalogChange(field.name, value)
                          }
                        />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item
                        label="Apply discount"
                        tooltip="Turn on to offer this catalog tab at a discounted renewal price (per currency).">
                        <Switch
                          checked={!!row.applyDiscount}
                          onChange={checked =>
                            setApplyDiscount(field.name, checked)
                          }
                        />
                      </Form.Item>
                    </Col>
                  </Row>

                  {row.applyDiscount ? (
                    <Form.List name={[field.name, 'discounts']}>
                      {(
                        discFields,
                        {add: addDiscount, remove: removeDiscount},
                      ) => (
                        <div className={styles.discountList}>
                          <div className={styles.discountListHeader}>
                            <span>Discounts by currency</span>
                            <Button
                              type="dashed"
                              size="small"
                              icon={<PlusOutlined />}
                              onClick={() => {
                                const tab = catalogTabs[field.name] || {};
                                const used = (tab.discounts || [])
                                  .map(d => d?.currency)
                                  .filter(Boolean);
                                const currency = nextCurrency(used);
                                const existing = lookupExistingDiscount(
                                  tab.catalogKey,
                                  currency,
                                );
                                addDiscount({
                                  type: existing?.type || 'FIXED',
                                  value: existing ? existing.value : null,
                                  currency,
                                });
                              }}>
                              Add currency
                            </Button>
                          </div>
                          {!discFields.length ? (
                            <p className={styles.emptyHint}>
                              No discounts yet. Add one per currency.
                            </p>
                          ) : null}
                          {discFields.map(discField => {
                            const disc =
                              (row.discounts || [])[discField.name] || {};
                            const isVariable = disc.type === 'VARIABLE';
                            // The catalog already has a saved discount for this
                            // currency — the field is pre-filled and editing it
                            // overrides that saved value for this offer.
                            const existing = lookupExistingDiscount(
                              row.catalogKey,
                              disc.currency,
                            );
                            return (
                              <Row
                                key={discField.key}
                                gutter={12}
                                className={styles.discountRow}>
                                <Col xs={12} md={5}>
                                  <Form.Item
                                    label="Currency"
                                    name={[discField.name, 'currency']}
                                    rules={[
                                      {required: true, message: 'Required'},
                                    ]}>
                                    <Select
                                      showSearch
                                      optionFilterProp="label"
                                      options={currencyOptions}
                                      onChange={value =>
                                        handleCurrencyChange(
                                          field.name,
                                          discField.name,
                                          value,
                                        )
                                      }
                                    />
                                  </Form.Item>
                                </Col>
                                <Col xs={12} md={5}>
                                  <Form.Item
                                    label="Type"
                                    name={[discField.name, 'type']}
                                    rules={[
                                      {required: true, message: 'Required'},
                                    ]}>
                                    <Select options={DISCOUNT_TYPES} />
                                  </Form.Item>
                                </Col>
                                <Col xs={12} md={5}>
                                  <Form.Item
                                    label={
                                      isVariable
                                        ? 'Value (%)'
                                        : 'Value (amount)'
                                    }
                                    name={[discField.name, 'value']}
                                    tooltip={
                                      existing
                                        ? 'This catalog already has a saved discount for this currency. Changing this value overrides the existing discount for this offer.'
                                        : undefined
                                    }
                                    extra={
                                      existing ? (
                                        <span className={styles.prefillHint}>
                                          Catalog has a saved discount for this
                                          currency
                                        </span>
                                      ) : undefined
                                    }
                                    rules={[
                                      {required: true, message: 'Required'},
                                      {
                                        type: 'number',
                                        min: isVariable ? 0 : 0.01,
                                        max: isVariable ? 100 : undefined,
                                        message: isVariable
                                          ? 'Use 0-100'
                                          : 'Must be > 0',
                                      },
                                    ]}>
                                    <InputNumber
                                      min={0}
                                      style={{width: '100%'}}
                                      placeholder={
                                        isVariable ? 'e.g. 20' : 'e.g. 5.00'
                                      }
                                    />
                                  </Form.Item>
                                </Col>
                                <Col xs={20} md={7}>
                                  <Form.Item label="New price (preview)">
                                    {renderPreview(field.name, discField.name)}
                                  </Form.Item>
                                </Col>
                                <Col
                                  xs={4}
                                  md={2}
                                  className={styles.discountDelete}>
                                  <Button
                                    type="text"
                                    danger
                                    icon={<DeleteOutlined />}
                                    onClick={() =>
                                      removeDiscount(discField.name)
                                    }
                                  />
                                </Col>
                              </Row>
                            );
                          })}
                        </div>
                      )}
                    </Form.List>
                  ) : null}
                </div>
              );
            })}
            <Button
              type="dashed"
              icon={<PlusOutlined />}
              onClick={() =>
                add({
                  catalogKey: undefined,
                  isDefault: fields.length === 0,
                  applyDiscount: false,
                  discounts: [],
                })
              }>
              Add catalog tab
            </Button>
          </div>
        )}
      </Form.List>
    </div>
  );
};
